'use client';
import React, { Component } from 'react';
import styled from 'styled-components';

import { Telemetry } from '../hooks/useTelemetry';
import localstorageService from '../LocalstorageService';
import {
  CriticalApplicationError,
  isLikelyBrowserExtensionCrash,
} from '@/typings/errors';
import { clearAllCookies } from '@/utils/cookies';

interface ErrorState {
  hasError: boolean;
}
interface ErrorProps {
  children: React.ReactNode;
}

class ErrorBoundary extends Component<ErrorProps, ErrorState> {
  constructor(props: ErrorProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      hasError: true,
    });

    // Create a CriticalApplicationError with the caught error as inner error
    const criticalError = new CriticalApplicationError(
      `Application render error: ${error.message}`,
      'client',
      error,
    );

    if (process.env.REACT_APP_ENV !== 'development') {
      // Client-side error reporting. The inner (original) error rides in
      // `context`, not just concatenated into criticalError.stack —
      // Faro's stack-trace parser only extracts `at ...` frame lines and
      // silently drops everything else, so a string-concatenated "Inner
      // Error:" section gets flattened together with the wrapper's own
      // frames with nothing indicating where one stack ends and the other
      // begins. `context` isn't touched by that parser, so it survives
      // as its own clearly-labeled field. React's componentStack — the
      // component tree path to where the render actually failed — is
      // included too; it was previously only ever console.error'd, never
      // reported anywhere.
      Telemetry()?.noticeError(criticalError, {
        innerErrorMessage: error.message,
        innerErrorStack: error.stack ?? 'no stack available',
        componentStack: errorInfo.componentStack ?? 'no component stack',
        // Queryable in Grafana instead of pattern-matching message text by
        // hand every time — see isLikelyBrowserExtensionCrash's own comment
        // for what this can and can't catch.
        likelyBrowserExtensionCrash: String(
          isLikelyBrowserExtensionCrash(error),
        ),
      });
    }

    // Log additional context for debugging
    // eslint-disable-next-line no-console
    console.error('Critical application error:', criticalError);
    // eslint-disable-next-line no-console
    console.error('Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <Container>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
              backgroundColor: '#F9F3EB',
            }}>
            <h2 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
              Oops, there is an error!
            </h2>
            <h3 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
              We have informed our developers, try again.
            </h3>
            <span
              style={{
                width: '100%',
                textAlign: 'center',
                margin: '0 20px 20px',
              }}>
              Browser extensions (like ad blockers or writing assistants) are a
              common cause — try disabling them, then try again.
            </span>
            <span style={{ width: '100%', textAlign: 'center' }}>
              If this persists, please have your teacher contact support
            </span>

            <button
              onClick={() => {
                window.location.reload();

                //Clear all local storage to start completely fresh
                localstorageService.clear();
                try {
                  clearAllCookies();
                } catch (e) {}
              }}
              style={{
                margin: '20px',
                border: 'none',
                backgroundColor: '#33A0DD',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '24px',
                padding: '20px',
                color: 'white',
              }}>
              Try again
            </button>
          </div>
        </Container>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;

const StyledButton = styled.button`
  padding: 20px;
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  flex-direction: column;
`;
